import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";
import { useFileOperations } from "@/composables/useFileOperations";
import { buildDownloadName, buildZipName } from "@/utils/downloadNames";

// Web Share with files: phones and some desktops. Probed with a stand-in file.
const canShareFiles = () => {
  try {
    return (
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({
        files: [new File([""], "x.jpg", { type: "image/jpeg" })],
      })
    );
  } catch (error) {
    return false;
  }
};
import { TEMPLATE_FETCH_PARAMS } from "@/constants";
import { debounce } from "lodash";
import { isDicomFile } from "@/utils/daikonUtils";
import UpgradeNoticeMixin from "@/mixins/UpgradeNoticeMixin";

export default {
  mixins: [UpgradeNoticeMixin],
  data() {
    return {
      lightBoxIndex: null,
      lightBoxList: [],
      isLoading: false,
      isFetching: false,
      limit: 100,
      selectedFiles: [],
      // Shown in the selection bar after a download; the selection stays so
      // the same files can be acted on again.
      downloadNotice: "",
      downloadNoticeTimer: null,
      // Files fetched for the share sheet when the phone wanted a fresh tap
      // to open it (the fetch outlasted the first tap).
      preparedShare: null,
      canShareFiles: canShareFiles(),
      storageListener: null,
    };
  },

  computed: {
    ...mapGetters({
      canGenerateDocumentForSign: "moduleBranch/canGenerateDocument",
      getFileList: "moduleMediaFile/getFileList",
      getFileTemplate: "moduleMediaFile/getFileTemplate",
      getTreatmentHistoryList: "moduleTreatment/getTreatmentHistoryList",
    }),
    // treatment id -> the day it was treated, for download names
    treatmentDates() {
      const dates = new Map();
      (this.getTreatmentHistoryList || []).forEach(t => {
        if (t && t.id) dates.set(t.id, t.creationDt);
      });
      return dates;
    },
    // The category every selected file shares, or null when they are mixed.
    selectionCategory() {
      const categories = new Set(this.selectedFiles.map(f => f.category));
      return categories.size === 1 ? [...categories][0] : null;
    },
    // What a zip of this gallery's files is called ("รูป", "เอกสาร").
    zipLabel() {
      return "ไฟล์";
    },
  },

  methods: {
    ...mapActions({
      fetchMediaFiles: "moduleMediaFile/fecthMediaFiles",
      deleteMediaFile: "moduleMediaFile/deleteMediaFile",
      fetchRadiiViewUrl: "moduleMediaFile/fetchRadiiViewUrl",
      fecthMediaFilesTemplate: "moduleMediaFile/fecthMediaFilesTemplate",
      addUploadedFiles: "moduleMediaFile/addUploadedFiles",
    }),

    handlePreview(item) {
      // Check if it's a DICOM file
      if (isDicomFile(item)) {
        // Open DICOM viewer in new tab
        this.handleDicomView(item);
        return;
      }
      
      const list = this.filteredList(item.category);
      this.lightBoxIndex = list.findIndex(i => i.id === item.id);
      this.lightBoxList = list;
    },

    handleDicomView(item) {
      // Open DICOM viewer in new tab
      const originalUrl = item.src || item.url;
      
      // Use base64 encoding to completely avoid URL encoding issues
      // This prevents AWS security token corruption from URL parsing
      const base64Url = btoa(originalUrl);
      
      const dicomViewerUrl = `/dicom-viewer?data=${base64Url}`;
      window.open(dicomViewerUrl, '_blank', 'noopener,noreferrer');
    },

    resetLightbox() {
      this.lightBoxIndex = null;
      this.lightBoxList = [];
    },

    async openRadiiView({ src }) {
      try {
        const { data } = await this.fetchRadiiViewUrl({ imageUrl: src });
        window.open(data.viewer, "_blank");
      } catch (err) {
        console.error(err);
      }
    },

    async handleDelete(item) {
      try {
        const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
          "ลบไฟล์นี้ ?",
          "คุณต้องการลบไฟล์นี้"
        );
        if (isConfirmed) {
          this.$set(item, "isDeleting", true);
          const res = await this.deleteMediaFile({
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            id: item.id,
          });
          if (res.status === 200) {
            this.selectedFiles = this.selectedFiles.filter(
              f => f.id !== item.id
            );
          }
        }
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.showAlert("ลบไฟล์ไม่สำเร็จ", "กรุณาลองใหม่อีกครั้ง");
      } finally {
        this.$set(item, "isDeleting", false);
      }
    },

    async handleBulkDelete() {
      try {
        const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
          "ลบไฟล์ที่เลือก ?",
          `คุณต้องการลบไฟล์ ${this.selectedFiles.length} ไฟล์`
        );

        if (isConfirmed) {
          const deletePromises = this.selectedFiles.map(async file => {
            this.$set(file, "isDeleting", true);
            try {
              const res = await this.deleteMediaFile({
                clinicUrl: this.$route.params.clinicUrl,
                branchUrl: this.$route.params.branchUrl,
                id: file.id,
              });
              return res.status === 200 ? file.id : null;
            } catch (err) {
              console.error(`Error deleting file ${file.id}:`, err);
              return null;
            } finally {
              this.$set(file, "isDeleting", false);
            }
          });

          const deletedIds = await Promise.all(deletePromises);
          const successfulDeletes = deletedIds.filter(id => id !== null);

          this.selectedFiles = this.selectedFiles.filter(
            file => !successfulDeletes.includes(file.id)
          );

          if (successfulDeletes.length > 0) {
            this.$bvToast.toast(
              `ลบไฟล์สำเร็จ ${successfulDeletes.length} ไฟล์`,
              {
                title: "สำเร็จ",
                variant: "success",
                solid: true,
                toaster: "b-toaster-bottom-left",
              }
            );
          }
        }
      } catch (err) {
        console.error("Error in bulk delete:", err);
        this.$refs.Dialog.showAlert("ลบไฟล์ไม่สำเร็จ", "กรุณาลองใหม่อีกครั้ง");
      }
    },

    // Progress shows in the global transfer toast (TransferProgressToast).
    setDownloadStatus(status) {
      this.$store.commit("moduleMediaFile/setDownloadStatus", status);
    },

    async handleDownload(item) {
      const { downloadFile } = useFileOperations();
      this.setDownloadStatus({ active: true, done: 0, total: 1, percent: 0, phase: "fetch" });
      try {
        await downloadFile(item, {
          fileName: this.downloadNameOf(item),
          refreshUrl: this.freshFileUrl,
          onProgress: this.setDownloadStatus,
        });
      } catch (error) {
        this.$refs.Dialog.showAlert("ดาวน์โหลดไม่สำเร็จ", error.message);
      } finally {
        this.setDownloadStatus({ active: false });
      }
    },

    async handleBulkDownload() {
      const files = this.selectedFiles;
      if (files.length === 0) return;
      const { bulkDownload } = useFileOperations();
      const zipName = buildZipName({ dn: this.patient.dn, label: this.zipLabel });
      this.setDownloadStatus({
        active: true,
        done: 0,
        total: files.length,
        percent: 0,
        phase: "fetch",
      });
      try {
        const { failed } = await bulkDownload(files, {
          fileNameOf: this.downloadNameOf,
          zipName,
          refreshUrl: this.freshFileUrl,
          onProgress: this.setDownloadStatus,
        });
        if (failed.length > 0) {
          this.$refs.Dialog.showAlert(
            "ดาวน์โหลดไม่ครบ",
            `ไม่สามารถดาวน์โหลด: ${failed.join(", ")}`
          );
        } else {
          this.showDownloadNotice(
            files.length > 1 ? `ดาวน์โหลดแล้ว ${zipName}.zip` : "ดาวน์โหลดแล้ว"
          );
        }
      } catch (error) {
        this.$refs.Dialog.showAlert(
          "ดาวน์โหลดไม่สำเร็จ",
          error.message || "กรุณาลองใหม่อีกครั้ง"
        );
      } finally {
        this.setDownloadStatus({ active: false });
      }
    },

    // Clinic forms belong to no patient, so they carry no DN.
    downloadNameOf(item) {
      return buildDownloadName(item, {
        dn: item.category === "Template" ? null : this.patient && this.patient.dn,
        treatmentDate: item.treatmentId
          ? this.treatmentDates.get(item.treatmentId)
          : null,
      });
    },

    // Presigned URLs expire after 15 minutes; a gallery left open longer
    // needs a fresh one. Refetches the list without replacing it on screen.
    async freshFileUrl(file) {
      try {
        const clinicUrl = this.$route.params.clinicUrl;
        const branchUrl = this.$route.params.branchUrl;
        const list =
          file.category === "Template"
            ? await this.fecthMediaFilesTemplate({
                clinicUrl,
                branchUrl,
                params: { ...TEMPLATE_FETCH_PARAMS, ephemeral: true },
              })
            : await this.fetchMediaFiles({
                clinicUrl,
                branchUrl,
                params: {
                  patientId: this.patient.id,
                  limit: this.limit,
                  ephemeral: true,
                },
              });
        const match = list.find(f => f.id === file.id);
        return match ? match.src : null;
      } catch (error) {
        return null;
      }
    },

    showDownloadNotice(text) {
      clearTimeout(this.downloadNoticeTimer);
      this.downloadNotice = text;
      this.downloadNoticeTimer = setTimeout(() => {
        this.downloadNotice = "";
      }, 5000);
    },

    clearSelection() {
      this.selectedFiles = [];
    },

    // Hands the selected files to the phone's share sheet (LINE, save to
    // Photos, Files…). Fetching can outlast the tap that asked for it; the
    // browser then refuses to open the sheet, and the bar offers a new tap.
    async handleShare() {
      if (this.preparedShare) {
        await this.openShareSheet(false);
        return;
      }
      const files = this.selectedFiles;
      if (files.length === 0) return;
      const { fetchFiles } = useFileOperations();
      this.setDownloadStatus({ active: true, done: 0, total: files.length, percent: 0, phase: "fetch" });
      try {
        const { files: fetched, failed } = await fetchFiles(files, {
          fileNameOf: this.downloadNameOf,
          refreshUrl: this.freshFileUrl,
          onProgress: this.setDownloadStatus,
        });
        if (fetched.length === 0) throw new Error("nothing fetched");
        if (failed.length > 0) {
          this.$refs.Dialog.showAlert("โหลดไฟล์ไม่ครบ", `แชร์ได้ไม่ครบ: ${failed.join(", ")}`);
        }
        this.preparedShare = fetched;
      } catch (error) {
        this.$refs.Dialog.showAlert("แชร์ไม่สำเร็จ", "โหลดไฟล์ไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
        return;
      } finally {
        this.setDownloadStatus({ active: false });
      }
      await this.openShareSheet(true);
    },

    async openShareSheet(firstTry) {
      const files = this.preparedShare;
      if (!files) return;
      try {
        if (!navigator.canShare({ files })) {
          const error = new Error("unsupported");
          error.name = "TypeError";
          throw error;
        }
        await navigator.share({ files });
        this.preparedShare = null;
      } catch (error) {
        if (error.name === "NotAllowedError" && firstTry) return;
        this.preparedShare = null;
        if (error.name === "AbortError") return;
        this.$refs.Dialog.showAlert(
          "แชร์ไม่สำเร็จ",
          "อุปกรณ์นี้แชร์ไฟล์ชุดนี้ไม่ได้ ลองบันทึกลงเครื่องแทน"
        );
      }
    },

    // Adds every file of the category to the selection, keeping the rest.
    selectAllIn(category) {
      const chosen = new Set(this.selectedFiles.map(f => f.id));
      const more = this.filteredList(category).filter(f => !chosen.has(f.id));
      this.selectedFiles = [...this.selectedFiles, ...more];
    },

    toggleFileSelection(file) {
      const index = this.selectedFiles.findIndex(f => f.id === file.id);
      if (index === -1) {
        this.selectedFiles.push(file);
      } else {
        this.selectedFiles.splice(index, 1);
      }
    },

    filteredList(category) {
      if (category === "Template") return this.getFileTemplate;
      return this.getFileList.filter(i => i.category === category);
    },

    /**
     * Shows files an upload just registered. createMediaFiles returns them
     * already registered, so they go straight into the gallery — no polling.
     * Only this patient's files (and clinic templates) belong here: the same
     * event reaches every open gallery.
     *
     * @param {Object[]} files - formatted media files
     */
    onFilesUploaded(files) {
      const mine = (files || []).filter(
        file =>
          file &&
          file.id &&
          (file.category === "Template" ||
            (this.patient && file.patientId === this.patient.id))
      );
      if (mine.length === 0) return;
      this.addUploadedFiles({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        files: mine,
      });
    },

    // A signing tab from before the complete endpoint only says "something
    // changed": fetch the list once instead of waiting on it.
    async refreshFiles() {
      try {
        await this.fetchMediaFiles({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          params: { patientId: this.patient.id, limit: this.limit },
        });
      } catch (error) {
        console.error("Error fetching files:", error);
      }
    },

    openSignTab(item) {
      // ปุ่มเซ็นทุกจุดล็อกพร้อมป้าย "อัปเกรด" อยู่แล้ว — ตรงนี้กันทางที่หลุดมา (ทุกการเซ็น
      // เข้าแฟ้มมารวมที่นี่) ใบรับรองแพทย์เซ็นผ่าน PdfExport ไม่ผ่านตรงนี้ จึงไม่โดนล็อก
      if (!this.canGenerateDocumentForSign) {
        this.showUpgradeNotice("การเซ็นเอกสารเก็บเข้าแฟ้มคนไข้");
        return;
      }
      const { clinicUrl, branchUrl } = this.$route.params;
      const url = `${process.env.VUE_APP_FRONT_BASE_URL}/document`;

      function objectToParams(obj) {
        return Object.keys(obj)
          .filter(key => obj[key] !== null && obj[key] !== undefined)
          .map(
            key => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key])
          )
          .join("&");
      }
      // SignDoc ตั้งชื่อเอกสารที่เซ็นแล้วจาก query filename/template — ชื่อคีย์ไม่ตรงกับ
      // ที่ mediaFile item มี (remark/category/id) จึงต้องแมปให้ ไม่ใช่หวังจาก spread
      // ไม่มี 2 คีย์นี้ SignDoc จะตกไปที่ชื่อ Signed_Document_<timestamp> ทุกครั้ง
      const params = {
        ...item,
        // ไม่ fallback ไป item.title: formatter ตั้ง title เป็นชื่อ category เมื่อไม่มี
        // remark ("Document") ซึ่งจะทำให้ไฟล์ที่ไม่มีชื่อทุกใบชื่อ "Document_signed"
        // ซ้ำกันหมด — ปล่อยเป็น null ให้ SignDoc ใช้ชื่อพร้อม timestamp แทน
        filename: item.remark || null,
        template: item.category === "Template" ? item.id : null,
        clinicUrl,
        branchUrl,
        patientId: this.patient?.id,
      };

      window.open(`${url}?${objectToParams(params)}`, "_blank");
    },

    showUploadModal(category) {
      this.$refs.uploadFiles.show({ category });
    },

    handleDirectUpload({ category, files }) {
      // For now, open the upload modal with the category
      // In the future, this could directly upload the files
      this.showUploadModal(category);
      
    },

    handleUploadSuccess({ files = [] }) {
      this.onFilesUploaded(files);
    },

    // Drop-zone rejections (no permission, wrong type or size) say why in `message`.
    handleUploadError(error) {
      console.error('Upload error:', error);
      this.$bvToast.toast(
        (error && error.message) || "เกิดข้อผิดพลาดในการอัปโหลดไฟล์",
        {
          title: "อัปโหลดล้มเหลว",
          variant: "danger",
          solid: true,
          autoHideDelay: 5000,
          toaster: "b-toaster-bottom-left",
        }
      );
    },

    handleStorageEvent(e) {
      if (e.key === "crossTabEvent" && e.newValue) {
        try {
          const eventData = JSON.parse(e.newValue);
          if (eventData.payload?.source !== "signDoc") return;

          if (eventData.type === "MEDIA_ADDED") {
            this.onFilesUploaded(eventData.payload.files);
            localStorage.removeItem("crossTabEvent");
          } else if (eventData.type === "POLL_FOR_UPDATE") {
            this.refreshFiles();
            localStorage.removeItem("crossTabEvent");
          }
        } catch (error) {
          console.error("Error processing storage event:", error);
        }
      }
    },
  },

  watch: {
    // Files prepared for sharing belong to the selection they came from.
    selectedFiles() {
      this.preparedShare = null;
    },
  },

  mounted() {
    eventBus.$on("mediaAdded", this.onFilesUploaded);

    this.storageListener = this.handleStorageEvent.bind(this);
    window.addEventListener("storage", this.storageListener);
  },

  beforeDestroy() {
    clearTimeout(this.downloadNoticeTimer);
    eventBus.$off("mediaAdded", this.onFilesUploaded);
    if (this.storageListener) {
      window.removeEventListener("storage", this.storageListener);
    }
  },
};